import { useEffect, useRef, useState } from 'react';
import type { AIModel, Attachment, Folder, Section } from '@volt/shared';
import { formatKopecks } from '@volt/shared';
import { ApiError } from '../api/client.js';
import {
  FILE_INPUT_ACCEPT,
  PHOTO_INPUT_ACCEPT,
  dataUrlToBase64,
  imageFileToDataUrl,
  readFileAsDataUrl,
} from '../lib/composerAttachments.js';
import { useAutoResize } from '../hooks/useAutoResize.js';
import { useEstimate } from '../hooks/useEstimate.js';
import { useGeneratedLibrary } from '../hooks/useGeneratedLibrary.js';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition.js';
import { useI18n } from '../i18n/context.js';
import { draftKey, loadDraft, saveDraft } from '../lib/drafts.js';
import { formatQuotedMessage } from '../lib/messageQuote.js';
import { hapticFeedback } from '../lib/haptics.js';
import { detectTouchInput, shouldSubmitOnEnter } from '../lib/composerInput.js';
import { useSession } from '../state/session.js';
import { useUI } from '../state/ui.js';
import { ContextMenu } from './ContextMenu.js';
import type { MenuItem } from './ContextMenu.js';
import { Icon } from './Icon.js';
import { IconButton } from './IconButton.js';
import { LibraryPickerModal } from './LibraryPickerModal.js';
import { ModelPicker } from './ModelPicker.js';
import { AUTO_MODEL_CHOICE_ID, providerLabel } from '../lib/modelPicker.js';

const PLACEHOLDER_KEYS: Record<Section, string> = {
  chat: 'composer.placeholder',
  image: 'composer.imagePlaceholder',
  video: 'composer.videoPlaceholder',
  video2: 'composer.videoPlaceholder',
  character: 'composer.characterPlaceholder',
  audio: 'composer.audioPlaceholder',
  voice: 'composer.voicePlaceholder',
  design: 'composer.placeholder',
};

interface PendingAttachment {
  id: string;
  fileName: string;
  mime: string;
  previewUrl?: string;
}

export interface ComposerProps {
  section: Section;
  conversationId: string | null;
  models: AIModel[];
  selectedModelChoice: string;
  resolvedModelId: string | undefined;
  onSelectModelChoice: (modelChoiceId: string) => void;
  streaming: boolean;
  thinking: boolean;
  onSend: (text: string, modelId: string, webSearch?: boolean, attachmentIds?: string[]) => void;
  onStop: () => void;
  /** Optional handler for the voice-mode button shown while the input is empty. */
  onVoiceMode?: () => void;
  folders?: Folder[];
  onMoveToFolder?: (folderId: string | null) => void;
  /** Selected message fragment the next message replies to. */
  quote?: string | null;
  onClearQuote?: () => void;
  /** Text to put into the input (e.g. a suggestion chip); `nonce` re-triggers identical text. */
  prefill?: { text: string; nonce: number } | null;
}

function uploadErrorCode(error: unknown): string {
  if (error instanceof ApiError) return error.code;
  return 'generic';
}

/** Message composer with model selector, voice input, cost estimate, and stop button. */
/** Chat attachments are images for now; the API accepts at most 10 per message. */
const LIBRARY_KINDS = ['image'] as const;
const MAX_CHAT_ATTACHMENTS = 10;

export function Composer({
  section,
  conversationId,
  models,
  selectedModelChoice,
  resolvedModelId,
  onSelectModelChoice,
  streaming,
  onSend,
  onStop,
  onVoiceMode,
  folders = [],
  onMoveToFolder,
  quote = null,
  onClearQuote,
  prefill = null,
}: ComposerProps) {
  const { t, locale } = useI18n();
  const { api } = useSession();
  const { toast } = useUI();
  const [text, setText] = useState('');
  const [pickerAnchor, setPickerAnchor] = useState<{ x: number; y: number } | null>(null);
  const pickerCloseAtRef = useRef<number>(0);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  // Generated content is only fetched once the user opens the attach menu.
  const [libraryArmed, setLibraryArmed] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const library = useGeneratedLibrary(LIBRARY_KINDS, { enabled: libraryArmed });
  useEffect(() => {
    if (attachMenuOpen) setLibraryArmed(true);
  }, [attachMenuOpen]);

  useEffect(() => {
    if (!attachMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAttachMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [attachMenuOpen]);
  const [webSearch, setWebSearch] = useState(false);
  const [folderPickerPos, setFolderPickerPos] = useState<{ x: number; y: number } | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [attaching, setAttaching] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prefersTouchInputRef = useRef(detectTouchInput());
  const plusBtnRef = useRef<HTMLDivElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useAutoResize(textareaRef, text, 9);

  // Per-conversation drafts (chat section only).
  const currentDraftKey = draftKey(conversationId);
  const skipSaveRef = useRef(true);
  useEffect(() => {
    if (section !== 'chat') return;
    skipSaveRef.current = true;
    setText(loadDraft(currentDraftKey));
  }, [currentDraftKey, section]);
  useEffect(() => {
    if (section !== 'chat') return;
    if (skipSaveRef.current) { skipSaveRef.current = false; return; }
    saveDraft(currentDraftKey, text);
  }, [text, currentDraftKey, section]);
  useEffect(() => {
    if (!prefill) return;
    setText(prefill.text);
    textareaRef.current?.focus({ preventScroll: true });
  }, [prefill]);

  useEffect(() => {
    if (quote) textareaRef.current?.focus({ preventScroll: true });
  }, [quote]);

  const { estimate, loading: estimating } = useEstimate(
    resolvedModelId,
    text,
    conversationId ?? undefined,
  );

  const {
    supported: speechSupported,
    listening,
    start: startSpeech,
    stop: stopSpeech,
  } = useSpeechRecognition(locale, (transcript, isFinal) => {
    setText((prev) => prev + (isFinal ? transcript : ''));
  });

  const selectedModel = models.find((m) => m.id === resolvedModelId);
  const supportsWebSearch = selectedModel?.supportsWebSearch ?? false;
  const selectedModelLabel =
    selectedModelChoice === AUTO_MODEL_CHOICE_ID
      ? `${t('composer.autoMode')}${selectedModel ? ` · ${providerLabel(selectedModel.providerCode)}` : ''}`
      : (selectedModel?.displayName ?? t('composer.selectModel'));

  function mergePendingAttachments(next: PendingAttachment[]) {
    if (next.length === 0) return;
    const nextIds = new Set(next.map((item) => item.id));
    setPendingAttachments((prev) => [...prev.filter((item) => !nextIds.has(item.id)), ...next]);
  }

  async function uploadPreparedAttachment(input: {
    fileName: string;
    mime: string;
    dataUrl: string;
  }): Promise<Attachment> {
    const response = await api.uploadAttachment({
      fileName: input.fileName,
      mime: input.mime,
      dataBase64: dataUrlToBase64(input.dataUrl),
    });
    return response.attachment;
  }

  async function handleImageFiles(files: ArrayLike<File> | null) {
    if (!files || files.length === 0) return;
    setAttaching(true);
    try {
      const uploaded = await Promise.all(
        Array.from(files).map(async (file) => {
          const prepared = await imageFileToDataUrl(file);
          const attachment = await uploadPreparedAttachment(prepared);
          return {
            id: attachment.id,
            fileName: attachment.fileName,
            mime: attachment.mime,
            previewUrl: prepared.dataUrl,
          } satisfies PendingAttachment;
        }),
      );
      mergePendingAttachments(uploaded);
    } catch (error) {
      toast(t(('error.' + uploadErrorCode(error)) as Parameters<typeof t>[0]), 'error');
    } finally {
      setAttaching(false);
    }
  }

  async function handleFileAttachments(files: FileList | null) {
    if (!files || files.length === 0) return;
    setAttaching(true);
    try {
      const uploaded = await Promise.all(
        Array.from(files).map(async (file) => {
          const dataUrl = await readFileAsDataUrl(file);
          const attachment = await uploadPreparedAttachment({
            fileName: file.name || 'file',
            mime: file.type || 'application/octet-stream',
            dataUrl,
          });
          return {
            id: attachment.id,
            fileName: attachment.fileName,
            mime: attachment.mime,
          } satisfies PendingAttachment;
        }),
      );
      mergePendingAttachments(uploaded);
    } catch (error) {
      toast(t(('error.' + uploadErrorCode(error)) as Parameters<typeof t>[0]), 'error');
    } finally {
      setAttaching(false);
    }
  }

  function handleSubmit(event?: React.FormEvent) {
    event?.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || streaming || attaching || !resolvedModelId) return;
    hapticFeedback('tap');
    onSend(
      formatQuotedMessage(quote, trimmed),
      resolvedModelId,
      supportsWebSearch && webSearch ? true : undefined,
      pendingAttachments.map((item) => item.id),
    );
    setText('');
    if (section === 'chat') saveDraft(currentDraftKey, '');
    setPendingAttachments([]);
    onClearQuote?.();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    const nativeEvent = event.nativeEvent;
    if (
      shouldSubmitOnEnter({
        key: event.key,
        shiftKey: event.shiftKey,
        isComposing: isComposing || nativeEvent.isComposing,
        keyCode: nativeEvent.keyCode,
        prefersTouchInput: prefersTouchInputRef.current,
      })
    ) {
      event.preventDefault();
      handleSubmit();
    }
  }

  const folderMenuItems: MenuItem[] = folders.map((f) => ({
    key: f.id,
    label: f.name,
    icon: 'folder' as const,
    onSelect: () => {
      onMoveToFolder?.(f.id);
    },
  }));

  const canSend = estimate?.canSend !== false;
  const belowMin = estimate?.reason === 'BELOW_MIN_BALANCE';
  const placeholder = t(PLACEHOLDER_KEYS[section] as Parameters<typeof t>[0]);

  return (
    <div className="composer">
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        aria-hidden="true"
        onChange={(event) => {
          void handleImageFiles(event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={photoInputRef}
        type="file"
        accept={PHOTO_INPUT_ACCEPT}
        multiple
        style={{ display: 'none' }}
        aria-hidden="true"
        onChange={(event) => {
          void handleImageFiles(event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={FILE_INPUT_ACCEPT}
        multiple
        style={{ display: 'none' }}
        aria-hidden="true"
        onChange={(event) => {
          void handleFileAttachments(event.target.files);
          event.target.value = '';
        }}
      />

      {pendingAttachments.length > 0 && (
        <div className="composer-attachments" aria-label={t('composer.attach')}>
          {pendingAttachments.map((attachment) => (
            <div
              key={attachment.id}
              className={`composer-attachment-chip${attachment.previewUrl ? ' is-image' : ''}`}
            >
              {attachment.previewUrl ? (
                <img src={attachment.previewUrl} alt="" className="composer-attachment-thumb" />
              ) : (
                <Icon name="attach" size={16} />
              )}
              <span className="composer-attachment-name">{attachment.fileName}</span>
              <button
                type="button"
                className="composer-attachment-remove"
                aria-label={t('common.close')}
                onClick={() =>
                  setPendingAttachments((prev) => prev.filter((item) => item.id !== attachment.id))
                }
              >
                <Icon name="close" size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <form className="composer-form" onSubmit={handleSubmit}>
        {quote && (
          <div className="composer-quote">
            <span className="composer-quote-text">{quote}</span>
            <button
              type="button"
              className="composer-attachment-remove"
              aria-label={t('common.close')}
              onClick={() => onClearQuote?.()}
            >
              <Icon name="close" size={14} />
            </button>
          </div>
        )}
        <textarea
          ref={textareaRef}
          className="composer-input"
          placeholder={listening ? t('composer.listening') : placeholder}
          value={text}
          rows={1}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          disabled={streaming || attaching}
          aria-label={placeholder}
        />

        <div className="composer-toolbar">
          <div className="composer-toolbar-left">
            <div className="composer-plus-wrap" ref={plusBtnRef}>
              <IconButton
                icon="plus"
                label={t('composer.attach')}
                size={18}
                onClick={() => setAttachMenuOpen((v) => !v)}
                disabled={attaching}
                aria-expanded={attachMenuOpen}
                aria-haspopup="menu"
              />
              {attachMenuOpen && (
                <div
                  className="plus-popover"
                  role="menu"
                  aria-label={t('composer.attachTitle')}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    role="menuitem"
                    className="plus-popover-item"
                    onClick={() => {
                      setAttachMenuOpen(false);
                      photoInputRef.current?.click();
                    }}
                  >
                    <Icon name="image" size={18} />
                    <span>{t('composer.attachPhotos')}</span>
                  </button>
                  {library.hasItems && (
                    <button
                      type="button"
                      role="menuitem"
                      className="plus-popover-item"
                      onClick={() => {
                        setAttachMenuOpen(false);
                        setLibraryOpen(true);
                      }}
                    >
                      <Icon name="folder" size={18} />
                      <span>{t('library.fromLibrary')}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    className="plus-popover-item"
                    onClick={() => {
                      setAttachMenuOpen(false);
                      cameraInputRef.current?.click();
                    }}
                  >
                    <Icon name="camera" size={18} />
                    <span>{t('composer.attachCamera')}</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="plus-popover-item"
                    onClick={() => {
                      setAttachMenuOpen(false);
                      fileInputRef.current?.click();
                    }}
                  >
                    <Icon name="attach" size={18} />
                    <span className="plus-popover-label">
                      {t('composer.attachFiles')}
                      <small className="plus-popover-hint">{t('composer.attachFilesHint')}</small>
                    </span>
                  </button>
                </div>
              )}
            </div>
            {attachMenuOpen && (
              <div
                className="attach-menu-overlay"
                onPointerDown={(e) => {
                  if (e.target === e.currentTarget) e.preventDefault();
                  setAttachMenuOpen(false);
                }}
                aria-hidden="true"
              />
            )}

            <button
              type="button"
              className="composer-model-btn"
              onClick={(event) => {
                setAttachMenuOpen(false);
                if (pickerAnchor !== null) {
                  setPickerAnchor(null);
                } else if (Date.now() - pickerCloseAtRef.current > 200) {
                  // Guard: don't reopen when the overlay just closed via tap-through
                  const rect = event.currentTarget.getBoundingClientRect();
                  setPickerAnchor({ x: rect.left, y: rect.top });
                }
              }}
              aria-label={t('composer.model')}
              title={selectedModelLabel}
            >
              <span className="composer-model-name">{selectedModelLabel}</span>
              <Icon name="chevronDown" size={12} />
            </button>

            {supportsWebSearch && (
              <button
                type="button"
                className={`chip composer-web-search-btn${webSearch ? ' is-active' : ''}`}
                onClick={() => setWebSearch((v) => !v)}
                aria-pressed={webSearch}
                title={webSearch ? t('composer.webSearchOn') : t('composer.webSearch')}
              >
                <Icon name="globe" size={12} />
              </button>
            )}
          </div>

          <div className="composer-toolbar-right">
            {estimating && <span className="spinner" style={{ width: 12, height: 12 }} />}
            {estimate && !estimating && (
              <span className={`estimate-chip${!canSend ? ' is-warn' : ''}`}>
                {canSend
                  ? t('composer.estimate', {
                      price: formatKopecks(
                        Math.round((estimate.minKopecks + estimate.maxKopecks) / 2),
                      ),
                    })
                  : belowMin
                    ? t('composer.topUpHint', {
                        amount: formatKopecks(estimate.minKopecks),
                      })
                    : t('error.INSUFFICIENT_BALANCE')}
              </span>
            )}

            <IconButton
              icon="mic"
              label={t('composer.voiceInput')}
              size={18}
              active={listening}
              onClick={() => {
                if (!speechSupported) {
                  toast(t('composer.dialogUnsupported'));
                  return;
                }
                if (listening) stopSpeech();
                else startSpeech();
              }}
              disabled={attaching}
            />

            {streaming ? (
              <IconButton icon="stop" label={t('composer.stop')} size={18} onClick={onStop} />
            ) : !text.trim() && pendingAttachments.length === 0 ? (
              <IconButton
                icon="waveform"
                className="voice-mode-btn"
                label={t('composer.voiceMode')}
                size={18}
                onClick={onVoiceMode}
                disabled={attaching || !onVoiceMode || !resolvedModelId}
              />
            ) : (
              <IconButton
                icon="send"
                label={t('composer.send')}
                size={18}
                type="submit"
                disabled={!text.trim() || !resolvedModelId || !canSend || attaching}
              />
            )}
          </div>
        </div>
      </form>

      <ModelPicker
        open={pickerAnchor !== null}
        onClose={() => {
          pickerCloseAtRef.current = Date.now();
          setPickerAnchor(null);
        }}
        models={models}
        section={section}
        selectedModelChoice={selectedModelChoice}
        resolvedModelId={resolvedModelId}
        onSelect={onSelectModelChoice}
      />

      <ContextMenu
        open={folderPickerPos !== null}
        x={folderPickerPos?.x ?? 0}
        y={folderPickerPos?.y ?? 0}
        items={
          folderMenuItems.length > 0
            ? folderMenuItems
            : [
                {
                  key: 'no-folders',
                  label: t('sidebar.newFolder'),
                  icon: 'folderPlus',
                  onSelect: () => setFolderPickerPos(null),
                },
              ]
        }
        onClose={() => setFolderPickerPos(null)}
      />

      <LibraryPickerModal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        library={library}
        maxSelect={Math.max(1, MAX_CHAT_ATTACHMENTS - pendingAttachments.length)}
        onPick={(files) => handleImageFiles(files)}
      />
    </div>
  );
}
